import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const cardVariants = cva(
  'rounded-lg bg-white shadow-sm transition-all',
  {
    variants: {
      border: {
        default: 'border border-gray-200',
        none: 'border-0',
        accent: 'border-2 border-blue-200',
      },
      hover: {
        none: '',
        lift: 'hover:shadow-md hover:-translate-y-0.5',
        glow: 'hover:shadow-lg hover:border-blue-300',
      },
    },
    defaultVariants: {
      border: 'default',
      hover: 'none',
    },
  }
);

export interface CardRootProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const CardRoot = React.forwardRef<HTMLDivElement, CardRootProps>(
  ({ className, border, hover, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge(cardVariants({ border, hover }), className)}
        {...props}
      />
    );
  }
);

CardRoot.displayName = 'CardRoot';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge('flex flex-col space-y-1.5 p-6', className)}
        {...props}
      />
    );
  }
);

CardHeader.displayName = 'CardHeader';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CardTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {}

export const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={twMerge(
          'text-2xl font-semibold leading-none tracking-tight',
          className
        )}
        {...props}
      />
    );
  }
);

CardTitle.displayName = 'CardTitle';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CardDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={twMerge('text-sm text-gray-600', className)}
      {...props}
    />
  );
});

CardDescription.displayName = 'CardDescription';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge('p-6 pt-0', className)}
        {...props}
      />
    );
  }
);

CardContent.displayName = 'CardContent';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge('flex items-center p-6 pt-0', className)}
        {...props}
      />
    );
  }
);

CardFooter.displayName = 'CardFooter';
